<template>
  <div
    id="vuethesearch"
    :class="[
      bemBlock,
      { 'the-search--show': visible },
      { 'interaction-active': interacting },
    ]"
    @keyup.esc="hide"
  >
    <TheSearchForm
      ref="form"
      v-model="value"
      :class="bemElement('form')"
      :autocomplete-source="autocompleteSource"
      :results="results"
      :search-source="searchSource"
      @searched="searchedHandler"
    />
    <BaseImageSearchPreview />
    <p v-if="showResultTitle" :class="bemElement('results-title')" />
    <BaseProductGrid
      v-if="visible"
      v-show="products && products.length > 0"
      ref="grid"
      :analytics="analyticsBase"
      :class="[
        bemElement('results'),
        {
          'u-scrollbar-byl': !infiniteScrollWrapper,
          'searched-animation': showSearchAnimation,
        },
      ]"
      :products="products"
      type="search"
      :infinite-wrapper="infiniteScrollWrapper"
      :infinite-next="true"
      @infinite:next="fetchNextPage"
      @mounted="initScrollMonitoring"
      @open-pdp-modal="openModal"
    />
    <ThePDPModal :product-tile="productForQuickview" @closed="closeModal" />
  </div>
</template>

<script>
// eslint-disable-next-line
///<reference path="../../../../../jsdoc-types.js" />

import { mapGetters, mapState } from 'vuex';
import { getters, types } from '../store';
import ThePDPModal from './ThePDPModal.vue';
import TheSearchForm from './TheSearchForm.vue';
import BaseImageSearchPreview from './BaseImageSearchPreview.vue';
import bemClassesMixin from '../mixins/bemClassesMixin';
import { defaultInfiniteScrollMixin } from '../mixins/infiniteScrollMixin';
import productSearchLatestProductIdsMixin from '../mixins/productSearchLatestProductIdsMixin';
import EventBusConfig from '../config/eventBusConfig';
import getAnalyticsEventBus from '../../common/getAnalyticsEventBus';
import getFrontConfig from '../../common/getFrontConfig';
import analyticsMixin from '../mixins/analyticsMixin';

const {
  searchAI: { isEnabledInFront },
} = getFrontConfig();

const scrollMonitor = require('../../common/monitors/scroll');

const analyticsEventBus = getAnalyticsEventBus();

// eslint-disable-next-line
__webpack_public_path__ = window.FRONT_CONFIG.urls.staticJs;

export default {
  name: 'TheSearch',

  components: {
    BaseProductGrid: () =>
      import(
        /* webpackChunkName: "baseproductgrid.js" */ './BaseProductGrid.vue'
      ),
    TheSearchForm,
    ThePDPModal,
    BaseImageSearchPreview,
  },

  mixins: [
    analyticsMixin,
    bemClassesMixin,
    defaultInfiniteScrollMixin,
    productSearchLatestProductIdsMixin,
  ],

  props: {
    autocompleteSource: {
      type: String,
      default: '/suggest',
    },
    defaults: {
      type: Object,
      default: () => ({
        hidden: true,
        value: '',
      }),
    },
    searchSource: {
      type: String,
      default: '/search',
    },
  },

  data() {
    return {
      hidden: this.defaults?.hidden ?? true,
      interacting: this.defaults?.interacting ?? false,
      infiniteScrollWrapper: this.defaults?.infiniteScrollWrapper ?? false,
      productForQuickview: null,
      value: this.defaults?.value ?? '',
      isSearchAIActive: isEnabledInFront,
    };
  },

  computed: {
    ...mapGetters({
      showSearchAnimation: getters.search.areFirstSearchResults,
    }),
    ...mapState({
      results: (state) => state.search.results,
      searchQuery: (state) => state.search.query,
      isCached: (state) => state.search.isCached,
    }),
    products() {
      if (this.results && this.results.products) {
        return this.results.products;
      }
      return [];
    },
    productSearch() {
      if (this.results.productSearch) {
        return this.results.productSearch;
      }
      return {};
    },
    trackedSearchText() {
      return this.results?.match ?? this.searchQuery;
    },
    visible() {
      return !this.hidden;
    },
    showResultTitle() {
      return (
        (this.isSearchAIActive ||
          window.FRONT_CONFIG?.searchAI?.isTestParticipant) &&
        !!this.productSearch?.isSearchAI &&
        !!this.productSearch?.searchAIData?.count &&
        !this.isImageSearch
      );
    },
    analyticsBase() {
      let searchedTerm;
      if (this.type === 'search') {
        searchedTerm =
          this.searchQuery ||
          analyticsEventBus.EVENTS.PAYLOADS.searchedTermNoText.type.image;
      }
      return {
        location: this.analyticsConst.location.search,
        searchedTerm,
      };
    },
  },

  watch: {
    productSearchLatestProductIds(ids) {
      if (ids.length > 0) {
        window.bylCQuotient?.trackViewProductInSearch(
          ids,
          this.trackedSearchText
        );
      }
    },
    visible(value) {
      this.calculateHeaderHeight(value);
    },
    $route() {
      if (this.visible) {
        this.hide();
      }
    },
  },

  mounted() {
    if (isEnabledInFront) {
      this.show();
    }
  },

  methods: {
    /**
     * Handle search event from TheSearchForm
     * @param {Object} searchData
     * @param {string} searchData.value
     * @param {ProductSearch} searchData.productSearch
     */
    searchedHandler(searchData) {
      this.infiniteReset(searchData.productSearch);

      // Wait to next tick to allow search suggestions elements to be added in DOM then recalculates header height
      this.$nextTick(() => {
        this.calculateHeaderHeight(true);
        if (Object.keys(searchData.productSearch).length > 0) {
          this.emitAnalyticsData();
        }
      });
    },
    calculateHeaderHeight(visible) {
      const header = document.querySelector('.header-group');
      const searchHeight = visible
        ? document.querySelector('.the-search__form').offsetHeight
        : 0;
      const headerPlusBannerHeight = header.offsetHeight + searchHeight;
      document.documentElement.style.setProperty(
        '--header-total-height-with-search',
        `${headerPlusBannerHeight}px`
      );
    },

    closeModal() {
      this.productForQuickview = null;
      this.emitAnalyticsData(true);
    },

    fetchNextPage($state) {
      const { DIRECTION_BOTTOM } = this.infinite.constants;
      this.infiniteHandle(
        $state,
        (url) => this.$store.dispatch(types.search.searchNextPage, url),
        DIRECTION_BOTTOM
      );
    },

    focus() {
      this.$refs.form.focus();
    },

    focusout() {
      this.$refs.form.focusout();
    },

    hide() {
      this.interacting = false;
      setTimeout(() => {
        this.hidden = true;
        this.$eventBus.$emit(EventBusConfig.events.TheSearch.hidden, this);
      }, 300);
    },

    initScrollMonitoring() {
      scrollMonitor.createScrollMonitor(this.$refs.grid?.$el);
    },

    openModal(product) {
      this.productForQuickview = product;
    },

    reset() {
      this.value = '';
    },

    show() {
      this.hidden = false;
      this.$eventBus.$emit(EventBusConfig.events.TheSearch.shown);
      this.$nextTick(() => {
        this.interacting = true;
        this.focus();
      });
    },

    toggle() {
      if (this.hidden) {
        this.show();
      } else {
        this.hide();
      }
    },

    /**
     * Emit data useful for analytics
     * @param {boolean} [isPageView=false] true if it is the first tracking of a
     * search
     */
    emitAnalyticsData(isPageView = false) {
      this.$eventBus.$emit(EventBusConfig.events.TheSearch.searched, {
        search: {
          query:
            this.searchQuery ||
            analyticsEventBus.EVENTS.PAYLOADS.searchedTermNoText.type.image,
          match: this.results?.match,
          count: this.results?.productSearch?.count,
          page: this.results?.productSearch?.pageNumber,
          isCached: this.isCached,
        },
        products: [...this.products],
        isPageView,
      });
    },
  },
};
</script>
